import { CircleDashed, EyeOff, Globe, Lock, Unplug } from 'lucide-react';
import { ConnectionsCard } from '../connections/ConnectionsCard';
import { Ring } from './charts';
import { useBusiness, useBusinessScore } from './context';
import { InternetCheck } from './InternetCheck';
import { VERIFIED_AT } from './progress';
import { NoAssessment } from './states';

/** Systems the business can't connect yet, shown so owners know what is coming. */
const LATER = [
  { name: 'Endpoint protection', cat: 'Device coverage, tamper protection, open alerts' },
  { name: 'Backup', cat: 'Backup jobs, restore tests, immutable copies' },
  { name: 'Firewall and VPN', cat: 'Firmware, VPN sign-in, exposed admin pages' },
];

/**
 * Connections, as the prototype lays them out: don't answer what can be discovered. How verified the
 * score is, declared against verified, what a connection may read, then the systems themselves.
 */
export function ConnectionsPage() {
  const { loading } = useBusiness();
  const { current: a, view } = useBusinessScore();
  if (loading) return <div className="empty">Loading…</div>;
  if (!a) return <NoAssessment />;
  const r = view.data?.score;
  const conf = r ? Math.round(r.controls.evidence_confidence * 100) : null;
  const verified = r ? r.controls.evidence_confidence >= VERIFIED_AT : false;

  return (
    <>
      <div className="bpage-head">
        <div>
          <h1>Connections</h1>
          <p>Don’t answer what we can discover. Read-only connections check your settings and keep your score current.</p>
        </div>
      </div>
      <div className="bgrid g-3">
        <section className="bcard">
          <div className="conf">
            <Ring value={conf ?? 0} color={verified ? 'var(--ok-strong)' : 'var(--warn-strong)'} />
            <div>
              <div className="eyebrow">Score confidence</div>
              <div className="stat-num">{conf === null ? '—' : `${conf}%`}</div>
              <p className="muted small">{verified ? 'Insurers treat this score as verified.' : `Insurers want ${VERIFIED_AT * 100}% or more.`}</p>
            </div>
          </div>
        </section>
        <section className="bcard">
          <div className="eyebrow">Declared vs verified</div>
          <div className="row gap-l mt-s">
            <div>
              <div className="stat-num big">{r ? Math.round(r.controls.declared) : '—'}</div>
              <div className="muted small">what you told us</div>
            </div>
            <div>
              <div className={`stat-num big ${r && r.controls.declared - r.controls.effective >= 15 ? 'warn-t' : 'ok-t'}`}>{r ? Math.round(r.controls.effective) : '—'}</div>
              <div className="muted small">what counts after evidence</div>
            </div>
          </div>
          <p className="muted small mt-s">The gap itself is a signal insurers look at.</p>
        </section>
        <section className="bcard">
          <div className="eyebrow">Your data</div>
          <div className="stack-s mt-s small">
            <div className="row gap-s"><Lock size={14} /><span>Read-only. We never change your settings.</span></div>
            <div className="row gap-s"><EyeOff size={14} /><span>We never read email content or files.</span></div>
            <div className="row gap-s"><Unplug size={14} /><span>Disconnect any time.</span></div>
          </div>
        </section>
      </div>
      <div className="bgrid g-3 mt-l integ-grid">
        <div className="integ on">
          <div className="row gap-s integ-head">
            <span className="lg m365">M365</span>
            <div className="grow">
              <b>Microsoft 365</b>
              <div className="muted small">Identity and email · MFA, legacy sign-in</div>
            </div>
          </div>
          <ConnectionsCard id={a.id} />
        </div>
        <div className="integ on internet-check">
          <div className="row gap-s integ-head">
            <span className="lg"><Globe size={16} /></span>
            <div className="grow">
              <b>Internet check</b>
              <div className="muted small">Your domain from outside · email protection, certificate, headers</div>
            </div>
          </div>
          <InternetCheck id={a.id} />
        </div>
        {LATER.map((g) => (
          <div key={g.name} className="integ later">
            <div className="row gap-s">
              <span className="lg"><CircleDashed size={16} /></span>
              <div className="grow">
                <b>{g.name}</b>
                <div className="muted small">{g.cat}</div>
              </div>
              <span className="bbadge">Not available yet</span>
            </div>
            <p className="muted small">Answer these questions in the Cyber Check for now. Connecting will check them for you once it is available.</p>
          </div>
        ))}
      </div>
    </>
  );
} // prettier-ignore
